import { useState } from 'react';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { Banner, useAnnouncer } from '../../components/Status';
import { givenName } from '../../domain/names';
import type { PersonWithNames } from '../../domain/types';
import { useI18n, type MessageKey } from '../../i18n';
import { failureKey } from '../../lib/results';
import { uuid } from '../../lib/uuid';
import { useRepos } from '../../repositories';
import { useApp } from '../../app/AppState';

/** A few openers, so nobody stares at an empty box. */
const PROMPTS: MessageKey[] = ['ask.prompt.meet', 'ask.prompt.child', 'ask.prompt.recipe', 'ask.prompt.advice'];

/**
 * "Ask {name} something": one box, a Send button. The question lands in the
 * bell of whoever records for that person and on their profile, and is
 * answered with an ordinary story.
 */
export function AskQuestionDialog({ person, open, onClose }: { person: PersonWithNames; open: boolean; onClose: () => void }) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { reload } = useApp();
  const { toast } = useAnnouncer();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = givenName(person, lang);

  const close = () => {
    setText('');
    setError(null);
    onClose();
  };
  const send = async () => {
    setBusy(true);
    setError(null);
    const r = await repos.stories.askQuestion(person.id, text, uuid());
    setBusy(false);
    if (r.ok) {
      toast(t('ask.sent', { name }));
      reload();
      close();
    } else setError(t(failureKey(r)));
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t('ask.title', { name })}
      actions={
        <button type="button" className="btn btn-primary" onClick={() => void send()} disabled={busy || !text.trim()}>
          <Icon name="question" />
          <span>{t('ask.send')}</span>
        </button>
      }
    >
      <div className="stack">
        {error && <Banner kind="error">{error}</Banner>}
        <p className="muted" style={{ margin: 0 }}>{t('ask.help', { name })}</p>
        <div className="chip-row">
          {PROMPTS.map((k) => (
            <button key={k} type="button" className="chip chip-btn" onClick={() => setText(t(k, { name }))}>
              {t(k, { name })}
            </button>
          ))}
        </div>
        <div className="field">
          <label className="field-label" htmlFor="ask-text">{t('ask.label')}</label>
          <textarea id="ask-text" className="input" rows={3} maxLength={240} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('ask.placeholder')} autoFocus />
        </div>
      </div>
    </Dialog>
  );
}
